import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Subscription } from 'rxjs';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { FormDefinitionService } from '../../core/services/form-definition.service';
import { FormService } from '../../core/services/form.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalConfig, ModalService } from '../../core/services/modal.service';
import { PermissionService } from '../../core/services/permission.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslationService } from '../../core/services/translation.service';
import { UserService } from '../../core/services/user.service';
import {
  ReportDefinition,
  REPORT_OPERATORS_BY_TYPE,
  ReportFilterOperator,
} from '../../core/models/report.model';
import { ConfirmsUnsavedChanges } from '../../core/guards/unsaved-changes.guard';
import { definitionsEqual } from '../../core/reports/report-definition.utils';
import { getReportableDynamicFields } from '../../core/reports/report-field.utils';
import { ReportSessionService } from '../../core/reports/report-session.service';
import { SavedReportService } from '../../core/reports/saved-report.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardActionsComponent } from '../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { DiscardChangesDialogComponent } from './discard-changes-dialog/discard-changes-dialog.component';
import {
  SaveReportAsDialogComponent,
  SaveReportAsModel,
} from './save-report-as-dialog/save-report-as-dialog.component';
import {
  AvailableField,
  ReportBuilderModel,
  VALUELESS_OPERATORS,
  definitionToModel,
  emptyBuilderModel,
  metadataFields,
  modelToDefinition,
} from './report-builder.model';

type Option = { label: string; value: string };

/**
 * Report Builder wizard (Phase 1).
 *
 * A Formly-driven 4-step wizard producing a runnable ReportDefinition:
 *   1. Dataset  — form, checkpoints, users, time range
 *   2. Columns  — output columns + sort
 *   3. Aggregation — none / count / sum
 *   4. Review   — plain-language summary + Run
 *
 * All inputs are Formly fields. The definition is handed to the results view via
 * {@link ReportSessionService}. Rich per-field dynamic filters build on the
 * already-implemented query engine in a later increment.
 */
@Component({
  selector: 'app-report-builder',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ButtonComponent,
    SpinnerComponent,
    HasPermissionDirective,
    TranslatePipe,
  ],
  templateUrl: './report-builder.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './report-builder.component.css',
})
export class ReportBuilderComponent implements OnInit, OnDestroy, ConfirmsUnsavedChanges {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private formService = inject(FormService);
  private checkpointService = inject(CheckPointService);
  private userService = inject(UserService);
  private formDefinitionService = inject(FormDefinitionService);
  private translate = inject(TranslationService);
  private session = inject(ReportSessionService);
  private savedReports = inject(SavedReportService);
  private permissions = inject(PermissionService);
  private toast = inject(ToastService);
  private modalService = inject(ModalService);
  private breadcrumbService = inject(BreadcrumbService);
  private log = inject(LoggerService).getLogger('report-builder');

  /** Set when editing an existing saved report. */
  readonly editingId = signal<string | null>(null);
  readonly saving = signal(false);

  readonly step = signal(0);
  readonly totalSteps = 4;
  readonly stepTitleKeys = [
    'reports.builder.steps.dataset',
    'reports.builder.steps.filtersColumns',
    'reports.builder.steps.aggregation',
    'reports.builder.steps.review',
  ];

  readonly form = new FormGroup({});
  model: ReportBuilderModel = emptyBuilderModel();

  private readonly forms = signal<Option[]>([]);
  private readonly checkpoints = signal<Option[]>([]);
  private readonly users = signal<Option[]>([]);
  private readonly dynamicFields = signal<AvailableField[]>([]);
  private readonly existingReportNames = signal<string[]>([]);
  private lastFormId?: string;
  /** The auto-generated name last written into the model, so we only keep
   * auto-filling the name while the user hasn't typed their own. */
  private lastAutoName?: string;
  /** Keeps the wizard step in sync with the `?step=` URL param, so a refresh
   *  or browser back/forward returns to the same step (see frontend/CLAUDE.md
   *  "URL-Restorable Navigation State"). */
  private queryParamsSub: Subscription | null = null;
  /** The last-saved (or null if never saved) definition, used to detect
   *  unsaved changes — see {@link isDirty}. */
  private baseline: ReportDefinition | null = null;
  /** Set immediately before a deliberate, non-destructive navigation (Run,
   *  Save, Save As) so {@link confirmDiscardIfDirty} lets it through without
   *  prompting — those actions carry the current criteria forward safely. */
  private bypassUnsavedChangesGuard = false;

  /** All fields that can be chosen as columns/sort/filters. */
  readonly availableFields = computed<AvailableField[]>(() => [
    ...metadataFields({ checkpoints: this.checkpoints(), users: this.users() }),
    ...this.dynamicFields(),
  ]);

  readonly fields = computed<FormlyFieldConfig[]>(() => {
    switch (this.step()) {
      case 0:
        return this.datasetFields();
      case 1:
        return this.columnsFields();
      case 2:
        return this.aggregationFields();
      default:
        return [];
    }
  });

  /**
   * Method (not computed): the Formly `model` is a plain mutated object, not a
   * signal, so this must be re-read on each change-detection pass.
   */
  canRun(): boolean {
    return !!this.model.formId;
  }

  /** Whether the current criteria differ from what's persisted (or, for a
   *  never-saved report, whether anything meaningful has been entered yet). */
  isDirty(): boolean {
    if (!this.model.formId) return false;
    const current = modelToDefinition(this.model, this.availableFields());
    return !definitionsEqual(current, this.baseline);
  }

  /** Runs `defaultColumnsIfEmpty`'s normalization against an arbitrary
   *  definition without touching component state — lets {@link loadSavedReport}
   *  capture a baseline that's directly comparable to `isDirty`'s snapshot,
   *  even though the live model may go through that same normalization. */
  private normalizedDefinition(def: ReportDefinition): ReportDefinition {
    const model = definitionToModel(def);
    if (model.columns.length === 0) {
      model.columns = this.availableFields().map((f) => f.ref);
    }
    return modelToDefinition(model, this.availableFields());
  }

  /** CanDeactivate hook (see `unsavedChangesGuard`): confirms with the user
   *  before leaving this route with unsaved criteria changes. */
  async confirmDiscardIfDirty(): Promise<boolean> {
    if (this.bypassUnsavedChangesGuard) {
      // One-shot: consumed here so a later, unrelated deactivation attempt
      // (e.g. after a blocked navigation) still gets a real dirty check.
      this.bypassUnsavedChangesGuard = false;
      return true;
    }
    if (!this.isDirty()) return true;
    return this.confirmDiscard();
  }

  private async confirmDiscard(): Promise<boolean> {
    const closeSignal = signal<{ action: string } | null>(null);
    const config: ModalConfig = {
      component: DiscardChangesDialogComponent,
      title: this.translate.translate('reports.discardChanges.title'),
      size: 'small',
      closeSignal,
      footerClassName: 'flex flex-wrap gap-md flex-responsive-xs col-2',
      actionButtons: [
        {
          id: 'cancel',
          label: this.translate.translate('button.cancel'),
          variant: 'secondary',
          onClick: async () => closeSignal.set({ action: 'cancel' }),
        },
        {
          id: 'discard',
          label: this.translate.translate('reports.discardChanges.leave'),
          className: 'ml-auto',
          variant: 'danger',
          onClick: async () => closeSignal.set({ action: 'discard' }),
        },
      ],
    };
    const result = await this.modalService.openModal(config);
    return result?.action === 'discard';
  }

  async ngOnInit(): Promise<void> {
    // Restore the wizard step from ?step= on load, and keep it in sync with
    // browser back/forward navigation. The URL is 1-based (matching the
    // step numbers shown in the UI), while `step` internally is 0-based.
    this.queryParamsSub = this.route.queryParams.subscribe((params) => {
      const urlStep = Number(params['step'] ?? 1) - 1;
      if (Number.isInteger(urlStep) && urlStep >= 0 && urlStep < this.totalSteps) {
        this.step.set(urlStep);
      }
    });

    const [forms, checkpoints, users, existingReports] = await Promise.all([
      this.formService.getAll(),
      this.checkpointService.getAll(),
      this.userService.getAll(),
      this.savedReports.getAllVisible(),
    ]);
    this.forms.set(forms.map((f) => ({ label: f.name, value: f.id ?? '' })));
    this.checkpoints.set(checkpoints.map((c) => ({ label: c.name, value: c.id ?? '' })));
    this.users.set(users.map((u) => ({ label: u.full_name || u.email, value: u.id })));
    this.existingReportNames.set(existingReports.map((r) => r.name));

    // Editing an existing saved report (route /reports/:id).
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      await this.loadSavedReport(id);
      return;
    }

    // Otherwise continue an in-progress (ad-hoc) report if one is in the session
    // — e.g. arriving here via "Edit" from the results view. "New Report" entry
    // points clear the session first, so a truly new report starts blank.
    const current = this.session.current();
    if (current) {
      this.editingId.set(current.id ?? null);
      this.model = definitionToModel(current);
      await this.onModelChange();
    }
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
  }

  private async loadSavedReport(id: string): Promise<void> {
    try {
      const saved = await this.savedReports.getById(id);
      if (!saved) {
        this.toast.error(this.translate.translate('reports.builder.notFound'));
        this.router.navigate(['/reports']);
        return;
      }
      this.editingId.set(saved.id);
      this.breadcrumbService.setDynamicLabel(saved.name);

      // Resume the in-progress (unsaved) draft if the session still holds one
      // for this same report — e.g. the user changed criteria, ran the report
      // without saving, then clicked "Edit report". Without this, reloading
      // straight from the saved definition would silently discard that draft.
      const draft = this.session.current();
      const useDraft = !!draft && draft.id === id;
      this.model = definitionToModel(useDraft ? draft : saved.definition);
      // Load dynamic fields for the report's form so columns/filters resolve.
      await this.onModelChange();
      // Baseline is always the persisted version — run through the same
      // normalization `onModelChange` applies to the live model, so a
      // freshly-loaded (non-draft) report compares as clean.
      this.baseline = this.normalizedDefinition(saved.definition);
    } catch (error) {
      this.log.error('Failed to load saved report', error);
      this.toast.error(this.translate.translate('reports.builder.notFound'));
      this.router.navigate(['/reports']);
    }
  }

  /** Whether the current user can persist reports. */
  canSave(): boolean {
    return this.permissions.has('reports.create') && this.canRun();
  }

  isEditing(): boolean {
    return this.editingId() !== null;
  }

  /** "Save As" is only offered once there's an existing saved report to branch
   *  from — for a brand-new report, plain Save already creates it. */
  canSaveAs(): boolean {
    return this.isEditing() && this.canSave();
  }

  /** Creates or updates the saved report, then returns to the reports home. */
  async save(): Promise<void> {
    if (!this.canSave() || this.saving()) return;
    this.saving.set(true);
    try {
      const definition = modelToDefinition(this.model, this.availableFields());
      const id = this.editingId();
      const saved = id
        ? await this.savedReports.update(id, definition)
        : await this.savedReports.create(definition);
      this.baseline = this.normalizedDefinition(saved.definition);
      this.toast.success(this.translate.translate('reports.builder.saved'));
      this.bypassUnsavedChangesGuard = true;
      this.router.navigate(['/reports']);
    } catch (error) {
      this.log.error('Failed to save report', error);
      this.toast.error(this.translate.translate('reports.builder.saveError'));
    } finally {
      this.saving.set(false);
    }
  }

  /** Saves the current criteria as a brand-new report under a name the user
   *  confirms is unique, leaving the original report untouched. */
  async saveAs(): Promise<void> {
    if (!this.canSaveAs() || this.saving()) return;

    const modelSignal = signal<SaveReportAsModel>({ name: this.model.name?.trim() ?? '' });
    const saveDisabled = signal<string | null>(null);
    const closeSignal = signal<{ action: string } | null>(null);
    const config: ModalConfig = {
      component: SaveReportAsDialogComponent,
      componentInputs: {
        modelSignal,
        existingNames: this.existingReportNames(),
        saveDisabledSignal: saveDisabled,
      },
      title: this.translate.translate('reports.saveAsDialog.title'),
      size: 'small',
      closeSignal,
      footerClassName: 'flex flex-wrap gap-md flex-responsive-xs col-2',
      actionButtons: [
        {
          id: 'cancel',
          label: this.translate.translate('button.cancel'),
          variant: 'secondary',
          onClick: async () => closeSignal.set({ action: 'cancel' }),
        },
        {
          id: 'save',
          label: this.translate.translate('button.save'),
          className: 'ml-auto',
          variant: 'primary',
          disabled: saveDisabled as any,
          onClick: async () => closeSignal.set({ action: 'save' }),
        },
      ],
    };

    const result = await this.modalService.openModal(config);
    if (result?.action !== 'save') return;

    const name = modelSignal().name.trim();
    this.saving.set(true);
    try {
      const definition = modelToDefinition(this.model, this.availableFields());
      definition.name = name;
      const created = await this.savedReports.create(definition);
      this.existingReportNames.update((names) => [...names, created.name]);
      this.toast.success(this.translate.translate('reports.builder.saved'));
      this.bypassUnsavedChangesGuard = true;
      this.router.navigate(['/reports']);
    } catch (error) {
      this.log.error('Failed to save report as a new copy', error);
      this.toast.error(this.translate.translate('reports.builder.saveError'));
    } finally {
      this.saving.set(false);
    }
  }

  /** Reloads dynamic fields whenever the selected form changes. */
  async onModelChange(): Promise<void> {
    if (this.model.formId === this.lastFormId) return;
    this.lastFormId = this.model.formId;

    // Suggest a name from the chosen form, but only while the user hasn't
    // typed their own — i.e. the name is still blank or still matches the
    // last suggestion we wrote in.
    if (!this.model.name || this.model.name === this.lastAutoName) {
      const formName = this.forms().find((f) => f.value === this.model.formId)?.label;
      this.lastAutoName = formName ? this.defaultReportName(formName) : undefined;
      this.model.name = this.lastAutoName ?? '';
    }

    if (!this.model.formId) {
      this.dynamicFields.set([]);
      this.defaultColumnsIfEmpty();
      return;
    }
    try {
      const definition = await this.formDefinitionForForm(this.model.formId);
      const dynamic = getReportableDynamicFields(definition).map<AvailableField>((f) => ({
        ref: `dynamic::${f.key}`,
        label: f.label,
        source: 'dynamic',
        key: f.key,
        fieldType: f.fieldType,
        options: f.options,
      }));
      this.dynamicFields.set(dynamic);
    } catch (error) {
      this.log.error('Failed to load form fields for report builder', error);
      this.dynamicFields.set([]);
    }
    this.defaultColumnsIfEmpty();
  }

  /** New reports start with every available column checked, rather than the
   *  silent 3-column fallback the query engine applies when none are selected
   *  (see DEFAULT_DETAIL_COLUMNS in report-query.builder.ts). Only fills in
   *  when the model has no explicit selection yet, so it never overrides a
   *  saved report's columns or a user's deliberate "select none". */
  private defaultColumnsIfEmpty(): void {
    if (this.model.columns.length === 0) {
      this.model.columns = this.availableFields().map((f) => f.ref);
    }
  }

  /** "<form name> report", disambiguated with "copy N" against reports the
   * user can already see, so a fresh report never silently shares a name. */
  private defaultReportName(formName: string): string {
    const base = `${formName} report`;
    const existing = new Set(this.existingReportNames());
    if (!existing.has(base)) return base;
    let n = 2;
    while (existing.has(`${base} copy ${n}`)) n++;
    return `${base} copy ${n}`;
  }

  private async formDefinitionForForm(formId: string) {
    const form = (await this.formService.getAll()).find((f) => f.id === formId);
    const definitionId = form?.form_definition_id;
    if (!definitionId) return null;
    return this.formDefinitionService.getFormDefinition(definitionId);
  }

  next(): void {
    if (this.step() < this.totalSteps - 1) {
      this.step.set(this.step() + 1);
      this.syncStepToUrl();
    }
  }

  previous(): void {
    if (this.step() > 0) {
      this.step.set(this.step() - 1);
      this.syncStepToUrl();
    }
  }

  /** Every step past the dataset step is gated on a form being chosen —
   *  mirrors the "Next" button's disabled condition on step 0. */
  canGoToStep(index: number): boolean {
    return index === 0 || this.canRun();
  }

  /** Lets the step labels act as tabs: jump straight to any already-reachable
   *  step instead of only moving one at a time via next()/previous(). */
  goToStep(index: number): void {
    if (index === this.step() || !this.canGoToStep(index)) return;
    this.step.set(index);
    this.syncStepToUrl();
  }

  /** Reflects the current step into `?step=`, so the URL round-trips through a
   *  refresh. The URL is 1-based to match the step numbers shown in the UI.
   *  Step 0 (shown as "1") is the default entry point, so it's omitted rather
   *  than written as `?step=1`, keeping the URL clean for the common case. */
  private syncStepToUrl(): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    if (this.step() > 0) {
      queryParams['step'] = String(this.step() + 1);
    } else {
      delete queryParams['step'];
    }
    this.router.navigate([], { relativeTo: this.route, queryParams });
  }

  back(): void {
    this.router.navigate(['/reports']);
  }

  run(): void {
    if (!this.canRun()) return;
    const definition = modelToDefinition(this.model, this.availableFields());
    // Carry the saved report's id through to the results view so its Save
    // button updates the existing report instead of creating a duplicate.
    const id = this.editingId();
    if (id) definition.id = id;
    this.session.set(definition);
    // Running doesn't abandon anything — the definition just set above is
    // exactly what the results view (and a subsequent "Edit report") will use.
    this.bypassUnsavedChangesGuard = true;
    // Saved reports run at a URL carrying their id, so the results page
    // survives a refresh or a shared link; ad-hoc reports have no id to put
    // in the URL and fall back to the session-only /reports/run route.
    this.router.navigate(id ? ['/reports', id, 'results'] : ['/reports/run']);
  }

  /** Plain-language summary shown on the Review step (method — reads model). */
  summaryLines(): string[] {
    const lines: string[] = [];
    const formName = this.forms().find((f) => f.value === this.model.formId)?.label;
    lines.push(`${this.t('reports.builder.summary.form')}: ${formName ?? '—'}`);
    lines.push(
      `${this.t('reports.builder.summary.timeRange')}: ${this.t('reports.timeRange.' + this.model.timeRangePreset)}`,
    );
    const filterCount = (this.model.filters ?? []).filter((r) => r.field && r.operator).length;
    lines.push(`${this.t('reports.builder.field.filters')}: ${filterCount}`);
    lines.push(
      `${this.t('reports.builder.summary.aggregation')}: ${this.t('reports.agg.' + this.model.aggregationMode)}`,
    );
    if (this.model.aggregationMode === 'none') {
      lines.push(
        `${this.t('reports.builder.summary.columns')}: ${this.model.columns?.length || this.t('reports.builder.summary.defaultColumns')}`,
      );
    }
    return lines;
  }

  private t(key: string): string {
    return this.translate.translate(key);
  }

  // --- Formly field builders -------------------------------------------------

  private datasetFields(): FormlyFieldConfig[] {
    return [
      {
        key: 'name',
        type: 'input',
        props: {
          label: this.t('reports.builder.field.name'),
          required: true,
        },
      },
      {
        key: 'formId',
        type: 'dropdown',
        props: {
          label: this.t('reports.builder.field.form'),
          required: true,
          options: this.forms(),
        },
      },
      {
        key: 'timeRangePreset',
        type: 'dropdown',
        props: {
          label: this.t('reports.builder.field.timeRange'),
          options: this.timeRangeOptions(),
        },
      },
      {
        key: 'customStart',
        type: 'datetime',
        props: { label: this.t('reports.builder.field.customStart') },
        expressions: { hide: () => this.model.timeRangePreset !== 'custom' },
      },
      {
        key: 'customEnd',
        type: 'datetime',
        props: { label: this.t('reports.builder.field.customEnd') },
        expressions: { hide: () => this.model.timeRangePreset !== 'custom' },
      },
      {
        key: 'latestPerInstanceEnabled',
        type: 'checkbox',
        props: {
          label: this.t('reports.builder.field.latestPerInstance'),
          description: this.t('reports.builder.field.latestPerInstanceHint'),
        },
      },
      {
        key: 'latestPerInstanceN',
        type: 'number',
        props: { label: this.t('reports.builder.field.latestPerInstanceN'), min: 1 },
        expressions: { hide: () => !this.model.latestPerInstanceEnabled },
      },
      {
        key: 'asOf',
        type: 'datetime',
        props: {
          label: this.t('reports.builder.field.asOf'),
          description: this.t('reports.builder.field.asOfHint'),
        },
        expressions: { hide: () => !this.model.latestPerInstanceEnabled },
      },
    ];
  }

  private columnsFields(): FormlyFieldConfig[] {
    const columnOptions = this.availableFields().map((f) => ({
      label: this.maybeTranslate(f.label),
      value: f.ref,
    }));
    return [
      this.filtersField(columnOptions),
      {
        key: 'columns',
        type: 'multi-checkbox',
        props: {
          label: this.t('reports.builder.field.columns'),
          description: this.t('reports.builder.field.columnsHint'),
          options: columnOptions,
        },
      },
      {
        key: 'sortColumn',
        type: 'dropdown',
        props: {
          label: this.t('reports.builder.field.sortColumn'),
          options: [
            { label: this.t('reports.builder.field.sortNone'), value: '' },
            ...columnOptions,
          ],
        },
      },
      {
        key: 'sortDirection',
        type: 'dropdown',
        props: {
          label: this.t('reports.builder.field.sortDirection'),
          options: [
            { label: this.t('reports.builder.field.sortDesc'), value: 'desc' },
            { label: this.t('reports.builder.field.sortAsc'), value: 'asc' },
          ],
        },
        expressions: { hide: () => !this.model.sortColumn },
      },
    ];
  }

  private aggregationFields(): FormlyFieldConfig[] {
    const allOptions = this.availableFields().map((f) => ({
      label: this.maybeTranslate(f.label),
      value: f.ref,
    }));
    const numericOptions = this.availableFields()
      .filter((f) => f.fieldType === 'number')
      .map((f) => ({ label: this.maybeTranslate(f.label), value: f.ref }));

    // sum/avg require a numeric target; min/max/distinct_count accept any field.
    const numericModes = ['sum', 'avg'];
    const anyFieldModes = ['min', 'max', 'distinct_count'];

    return [
      {
        key: 'aggregationMode',
        type: 'dropdown',
        props: {
          label: this.t('reports.builder.field.aggregation'),
          options: [
            { label: this.t('reports.agg.none'), value: 'none' },
            { label: this.t('reports.agg.count'), value: 'count' },
            { label: this.t('reports.agg.sum'), value: 'sum' },
            { label: this.t('reports.agg.avg'), value: 'avg' },
            { label: this.t('reports.agg.min'), value: 'min' },
            { label: this.t('reports.agg.max'), value: 'max' },
            { label: this.t('reports.agg.distinct_count'), value: 'distinct_count' },
          ],
        },
      },
      {
        // numeric target for sum/avg
        key: 'aggregationTarget',
        type: 'dropdown',
        props: {
          label: this.t('reports.builder.field.aggregationTarget'),
          description: this.t('reports.builder.field.aggregationTargetHint'),
          options: numericOptions,
        },
        expressions: { hide: () => !numericModes.includes(this.model.aggregationMode) },
      },
      {
        // any-field target for min/max/distinct_count (shares the same model key)
        key: 'aggregationTarget',
        type: 'dropdown',
        props: {
          label: this.t('reports.builder.field.aggregationTargetAny'),
          options: allOptions,
        },
        expressions: { hide: () => !anyFieldModes.includes(this.model.aggregationMode) },
      },
      {
        key: 'groupByColumn',
        type: 'dropdown',
        props: {
          label: this.t('reports.builder.field.groupBy'),
          description: this.t('reports.builder.field.groupByHint'),
          options: [
            { label: this.t('reports.builder.field.groupByNone'), value: '' },
            ...allOptions,
          ],
        },
        expressions: { hide: () => this.model.aggregationMode === 'none' },
      },
    ];
  }

  /**
   * The filter-row repeater. Operator options and value visibility react to
   * each row's selected field via Formly expressions.
   */
  private filtersField(fieldOptions: Option[]): FormlyFieldConfig {
    return {
      key: 'filters',
      type: 'report-filter-list',
      props: { label: this.t('reports.builder.field.filters') },
      fieldArray: {
        fieldGroup: [
          {
            key: 'field',
            type: 'dropdown',
            props: { label: this.t('reports.builder.filters.field'), options: fieldOptions },
          },
          {
            key: 'operator',
            type: 'dropdown',
            props: { label: this.t('reports.builder.filters.operator'), options: [] },
            expressions: {
              'props.options': (f: FormlyFieldConfig) => this.operatorOptions(f.model?.['field']),
            },
          },
          {
            key: 'value',
            type: 'report-filter-value',
            props: {
              label: this.t('reports.builder.filters.value'),
              description: this.t('reports.builder.filters.valueHint'),
              // Lets the value editor resolve the selected field's type + options
              // so enum fields render a checklist / select instead of a text box.
              resolveField: (ref: string) => this.availableFields().find((f) => f.ref === ref),
            },
            expressions: {
              hide: (f: FormlyFieldConfig) => {
                const op = f.model?.['operator'] as ReportFilterOperator | undefined;
                return !op || VALUELESS_OPERATORS.includes(op);
              },
            },
          },
        ],
      },
    };
  }

  /** Operators valid for the field referenced by `fieldRef`, as select options. */
  private operatorOptions(fieldRef: string | undefined): Option[] {
    if (!fieldRef) return [];
    const field = this.availableFields().find((f) => f.ref === fieldRef);
    if (!field) return [];
    return REPORT_OPERATORS_BY_TYPE[field.fieldType].map((op) => ({
      label: this.t('reports.operator.' + op),
      value: op,
    }));
  }

  private timeRangeOptions(): Option[] {
    const presets = [
      'all_time',
      'today',
      'yesterday',
      'this_week',
      'last_week',
      'this_month',
      'last_month',
      'last_7_days',
      'last_30_days',
      'custom',
    ];
    return presets.map((p) => ({ label: this.t('reports.timeRange.' + p), value: p }));
  }

  /** Metadata labels are i18n keys; dynamic labels are literal — translate keys only. */
  private maybeTranslate(label: string): string {
    return label.startsWith('reports.') ? this.t(label) : label;
  }
}
